import { IFormStep } from '../../utils/formStep';
import React, { ChangeEvent, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import {
    accountFromFormResult,
    handleInputWithLabelOnBlur,
    handleInputWithLabelOnFocus,
    startOverId,
    termsAndConditionsEn,
    termsAndConditionsFr,
} from '../../registration';
import Contact from '../../contact/step1/contact';
import ProfessionalStep1PracticeLocation from '../step1/professionalStep1PracticeLocation';
import ProfessionalStep2ProviderProfile from '../step2/professionalStep2ProviderProfile';
import ProfessionalStep2Reasons from '../step2/professionalStep2Reasons';
import ProfessionalStep2License from '../step2/professionalStep2License';
import ProfessionalStep2BusinessInformations from '../step2/professionalStep2BusinessInformation';
import { LocationModel } from '../../../../helpers/models/locationModel';
import fetchFormData from '../../../../helpers/fetchFormData';
import { updateAccount } from '../../../../helpers/account';
import { isErrorModel } from '../../../../helpers/models/errorModel';
import { getAbsoluteRouteForCurrentHost, ROUTE_CONFIRMATION } from '../../../../routes/routes';
import { REFERENCE_NO_PARAM } from '../../../../helpers/network';
import { IAccountModel } from '../../../../helpers/models/accountModel';
import { handleGenericFormError, scrollToFormErrors } from '../../utils/formErrors';
import { IRoleModel } from '../../../../helpers/models/roleModel';
import { IProvinceModel } from '../../../../helpers/models/provinceModel';
import { ReactComponent as NotificationLogo } from '../../../../styles/img/icons/error.svg';

const ProfessionalReviseSubmit = (props: IFormStep) => {
    const { t, account, i18n, startOver } = props;
    const locations = useRef<LocationModel[]>(
        account.locations.length > 0
            ? (account.locations as LocationModel[])
            : [new LocationModel()]
    );

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        const data = fetchFormData(e);

        const formResult = Object.assign({}, ...data.map((x) => ({ [x.name]: x.value })));
        const authorization = data.filter((element: any) => {
            return element.name === 'form_authorization_communication_submit';
        });

        const errorList = handleGenericFormError(authorization, t);

        // Send data to backend
        if (!errorList.length) {
            const accountDetails = accountFromFormResult(formResult, account);
            accountDetails.registrationComplete = true;

            // update account
            const result = await updateAccount(i18n.language, account.id, { ...accountDetails });
            if (isErrorModel(result)) {
                errorList.push({ message: 'form_generic_error', weight: 0 });
                scrollToFormErrors();
            } else {
                // Lead to result
                window.location.replace(
                    getAbsoluteRouteForCurrentHost(ROUTE_CONFIRMATION) +
                        `?${REFERENCE_NO_PARAM}=${(result as IAccountModel).referenceNo}`
                );
            }
        } else {
            document.getElementById('revise-submit-error')!.style.display = 'flex';
        }
        props.setError(errorList);
    };
    const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.value) {
            document
                .getElementById('form_authorization_communication_submit_label')!
                .classList.remove('registration-error');
            document.getElementById('revise-submit-error')!.style.display = 'none';
        }
    };
    const handleUpdateStep = (step: number) => {
        const newFormData = Object.assign({}, account);
        if (newFormData.step >= 1) {
            newFormData.step = step;
        }

        // temporary
        newFormData.dateLastAction = new Date().toISOString().split('T')[0];

        // set number to province instead of the returned object
        newFormData.province = +(account.province as IProvinceModel).id;
        newFormData.role = +(account.role as IRoleModel).id;
        updateAccount(i18n.language, account.id, { ...newFormData });
    };

    return (
        <form className="registration-container" onSubmit={handleSubmit}>
            <div className="registration-form-revise-submit-section">
                <Contact readonly account={account} />
            </div>
            <div className="registration-form-revise-submit-section registration-form-revise-submit-section--white">
                <div className="registration-form-revise-submit--modify-section">
                    <h1 className="header-3 header-3--purple registration_form_header">
                        {t('form_revise_submit_professional_step_1_profile_title')}
                    </h1>
                    <a
                        data-step={1}
                        className="standalone-link registration_form--modify-button"
                        tabIndex={0}
                        role="button"
                        onClick={(e) => {
                            handleUpdateStep(1);
                        }}
                        aria-label={t('form_revise_submit_professional_step_1_profile_title')
                            .addSpace()
                            .concat(t('form_revise_submit_professional_step_1_modify_btn'))}
                    >
                        {t('form_revise_submit_professional_step_1_modify_btn')}
                        <span className="registration-from--modify-button-icon"></span>
                    </a>
                </div>
                {locations.current.map((item, index) => {
                    return (
                        <div key={`professional_step_1_${index}`}>
                            {index > 0 && (
                                <hr
                                    key={`professional_step_1_title_${index}`}
                                    className="registration-professional-section-divider"
                                    aria-hidden="true"
                                />
                            )}
                            <ProfessionalStep1PracticeLocation
                                key={`professional_step_1_form_${index}`}
                                index={index}
                                account={props.account}
                                readonly
                            />
                        </div>
                    );
                })}
            </div>
            <div className="registration-form-revise-submit-section">
                <ProfessionalStep2ProviderProfile
                    account={account}
                    readonly
                    updateStep={(e: any) => handleUpdateStep(e.target.dataset.step)}
                />
                <ProfessionalStep2Reasons readonly account={account} />
                <hr aria-hidden="true" className="registration-professional-section-divider" />
                <ProfessionalStep2License
                    account={account}
                    readonly
                    provider={+(account.provider[0]?.role as IRoleModel)?.id}
                />
                <ProfessionalStep2BusinessInformations readonly account={account} />
            </div>
            <div className="registration-form-revise-submit-section registration-form-revise-submit-section--white">
                <label
                    id="form_authorization_communication_submit_label"
                    htmlFor="form_authorization_communication_submit"
                    className="checkbox form_authorization_eligible_label"
                >
                    <div>
                        {t('form_revise_submit_professional_field_communication')}
                        <a
                            className="standalone-link"
                            href={
                                i18n.language === 'en' ? termsAndConditionsEn : termsAndConditionsFr
                            }
                            target="_blank"
                            rel="noreferrer"
                        >
                            {t('form_revise_submit_professional_field_communication_terms')}
                        </a>
                    </div>
                    <input
                        className="checkbox-input field-value"
                        type="checkbox"
                        id="form_authorization_communication_submit"
                        name="form_authorization_communication_submit"
                        onBlur={handleInputWithLabelOnBlur}
                        onFocus={handleInputWithLabelOnFocus}
                        onChange={handleCheckboxChange}
                    />
                    <span className="checkmark checkmark--disabled"></span>
                </label>
                <div id="revise-submit-error" className="radio-button-error">
                    <NotificationLogo className="radio-button-error-icon" />
                    <span className="field-value radio-button-error-value registration-error">
                        Required
                    </span>
                </div>
                <div className="registration-professional-button-container">
                    <button
                        className="button button--secondary registration-professional-button"
                        onClick={startOver}
                        id={startOverId}
                    >
                        {t('form_revise_submit_button_restart')}
                    </button>
                    <div className="registration-professional-standalone-button-container">
                        <a
                            data-step={2}
                            tabIndex={0}
                            className="standalone-link registration-button-standalone-link registration-professional-standalone-link"
                            onClick={props.goBack}
                            role="button"
                        >
                            {t('form_revise_submit_professional_step_2_button_previous')}
                        </a>
                        <button
                            className="button button--primary registration-professional-button"
                            type="submit"
                        >
                            {t('form_revise_submit_button_submit')}
                        </button>
                    </div>
                </div>
            </div>
        </form>
    );
};

export default withTranslation()(ProfessionalReviseSubmit);
